import { catalogBadges } from "@/lib/catalog-presentation";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useMemo, useState } from "react";
import { MapPin, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { normalizeLang } from "@/i18n";

import { listUnifiedCatalog } from "@/lib/catalog.functions";
import {
  STATE_CATALOG,
  CITY_CATALOG,
  productsForState,
  productsForCity,
  sortProducts,
  SORT_OPTIONS,
  type SortKey,
} from "@/lib/tour-search";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { UnifiedTourCard } from "@/components/site/UnifiedTourCard";

const productsQuery = (lang: string) =>
  queryOptions({
    queryKey: ["catalog", "unified", lang],
    queryFn: () => listUnifiedCatalog({ data: { lang } }),
    staleTime: 5 * 60 * 1000,
  });

export const Route = createFileRoute("/destinos/$state")({
  beforeLoad: ({ params }) => {
    if (!STATE_CATALOG[params.state]) throw notFound();
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(productsQuery(context.lang)),
  head: ({ params }) => {
    const s = STATE_CATALOG[params.state];
    if (!s) return { meta: [{ title: "Destino — Mostrando México" }] };
    const title = `Tours en ${s.name} — Experiencias por entidad federativa | Mostrando México`;
    const desc = `Reserva los mejores tours en ${s.name}. ${s.tagline}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "place" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: `https://www.mostrandomexico.mx/destinos/${params.state}` }],
    };
  },
  component: StatePage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-center text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</div>
  ),
  notFoundComponent: () => (
    <div className="p-8 text-center text-sm text-muted-foreground">Estado no encontrado.</div>
  ),
});

function StatePage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <Suspense fallback={<div className="h-96" />}>
        <StateBody />
      </Suspense>
      <SiteFooter />
    </div>
  );
}

function StateBody() {
  const { state } = Route.useParams();
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const cfg = STATE_CATALOG[state];
  const { data } = useSuspenseQuery(productsQuery(lang));
  const [sort, setSort] = useState<SortKey>("featured");

  const matches = useMemo(
    () => sortProducts(productsForState(data.tours, state), sort),
    [data.tours, state, sort],
  );

  const citiesInState = useMemo(
    () =>
      cfg.cities
        .map((slug) => ({
          slug,
          entry: CITY_CATALOG[slug],
          count: productsForCity(data.tours, slug).length,
        }))
        .filter((c) => c.entry)
        .sort((a, b) => b.count - a.count),
    [data.tours, cfg.cities],
  );

  return (
    <>
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-br from-primary/8 via-surface to-accent/40">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_50%_at_50%_0%,oklch(0.72_0.18_45/0.18),transparent_70%)]" />
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-11">
          <Link
            to="/destinos"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground transition-colors hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" /> {t("catalogPage.allDestinations")}
          </Link>
          <div className="mt-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary">
            <MapPin className="h-3.5 w-3.5" /> {t("catalogPage.stateLabel")}
          </div>
          <h1 className="mt-1.5 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            {cfg.name}
          </h1>
          <p className="mt-2 max-w-2xl text-base text-muted-foreground">
            {t(`destTagline.${state}`, { defaultValue: cfg.tagline })}
          </p>
          <p className="mt-1.5 text-sm font-semibold text-foreground/80">
            {matches.length}{" "}
            {matches.length === 1
              ? t("catalogPage.availableOne")
              : t("catalogPage.availableMany")}
          </p>

          {citiesInState.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {citiesInState.map(({ slug, entry, count }) => (
                <Link
                  key={slug}
                  to="/tours/$city"
                  params={{ city: slug }}
                  className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground/80 transition hover:border-primary hover:text-primary"
                >
                  {entry.name} {count > 0 && <span className="text-muted-foreground">· {count}</span>}
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-7 sm:px-6 sm:py-9 lg:px-8 lg:py-12">
        {matches.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
            <p className="font-display text-xl">
              {t("catalogPage.comingSoon", { name: cfg.name })}
            </p>
            <Link
              to="/tours"
              className="mt-6 inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              {t("catalogPage.seeAllTours")}
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t("catalogPage.sortBy")}
              </p>
              <div className="hide-scrollbar inline-flex max-w-full overflow-x-auto rounded-full bg-muted p-1 text-xs font-semibold">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setSort(opt.key)}
                    className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 transition ${
                      sort === opt.key
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {t(`catalogPage.sort${opt.key.charAt(0).toUpperCase()}${opt.key.slice(1)}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {matches.map((p, i) => (
                <UnifiedTourCard key={`${p.source}-${p.id}`} tour={p} badge={catalogBadges(matches).get(p.id)} priority={i < 2} />
              ))}
            </div>
          </>
        )}
      </section>
    </>
  );
}
